import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { appColors } from '../../Config/Theme';
import { useAuth } from '../../Contexts/AuthProvider';
import { useSecurity } from '../../Contexts/SecurityProvider';
import exponentLogoWhite from '../../Exponent_Logo_White.png';

export const HEADER_HEIGHT = 44;

interface AppHeaderProps {
  onMenuToggle?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ onMenuToggle }) => {
  const { user, signOut } = useAuth();
  const { security } = useSecurity();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const menuOpen = Boolean(anchorEl);

  const displayName = user?.displayName?.trim() || 'User';
  const email = user?.email?.trim() || '';
  const roleSummary = security?.roleNames?.length ? security.roleNames.join(', ') : '';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleCloseMenu();

    // Guard against a misconfigured environment: the Cognito Hosted UI logout
    // flow requires the post-logout redirect URI to be present.
    if (!process.env.REACT_APP_REDIRECT_SIGNOUT) {
      console.error('Sign out is not configured: REACT_APP_REDIRECT_SIGNOUT is missing.');
      setErrorMessage('Sign out is not configured correctly.');
      return;
    }

    setSigningOut(true);
    try {
      // signOut clears local auth/security/query state and redirects through the
      // Cognito Hosted UI logout flow. On success the browser navigates away.
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      setSigningOut(false);
      setErrorMessage('Something went wrong signing out. Please try again.');
    }
  };

  return (
    <Box
      component="header"
      sx={{
        height: HEADER_HEIGHT,
        minHeight: HEADER_HEIGHT,
        bgcolor: appColors.header.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, sm: 2.5 },
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        zIndex: 1300,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
      }}
    >
      {/* ── Left: hamburger (mobile) + Exponent logo + app name ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
        {onMenuToggle && (
          <IconButton
            aria-label="Open navigation"
            onClick={onMenuToggle}
            size="small"
            sx={{
              color: appColors.header.text,
              display: { xs: 'flex', md: 'none' },
              mr: -0.5,
            }}
          >
            <MenuIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <Box
          component="img"
          src={exponentLogoWhite}
          alt="Exponent"
          sx={{ height: { xs: 18, sm: 24 }, flexShrink: 0 }}
        />
        <Box
          sx={{
            width: '1px',
            height: 20,
            bgcolor: appColors.header.border,
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: appColors.header.text,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Emergency Evacuation Accountability System (EEAS)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: appColors.header.text,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            display: { xs: 'block', sm: 'none' },
          }}
        >
          EEAS
        </Typography>
      </Box>

      {/* ── Right: user menu ── */}
      <ButtonBase
        id="user-menu-button"
        onClick={handleOpenMenu}
        disabled={signingOut}
        aria-label="User menu"
        aria-controls={menuOpen ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? true : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          '&.Mui-focusVisible': { outline: `2px solid ${appColors.header.text}`, outlineOffset: 2 },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: appColors.header.text,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {displayName}
        </Typography>
        {signingOut ? (
          <CircularProgress size={30} thickness={5} sx={{ color: appColors.header.text }} />
        ) : (
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: appColors.header.text,
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        )}
        <ArrowDropDownIcon
          sx={{
            color: appColors.header.text,
            fontSize: 22,
            ml: -0.75,
            transition: 'transform 0.2s ease',
            transform: menuOpen ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { 'aria-labelledby': 'user-menu-button', dense: true },
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 220,
              maxWidth: 300,
              // Opaque light teal matching the selected left-nav item (reuses the
              // same translucent token, composited over an opaque surface).
              bgcolor: appColors.surface.card,
              backgroundImage: `linear-gradient(${appColors.sidebar.selectedBg}, ${appColors.sidebar.selectedBg})`,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }} noWrap>
            {displayName}
          </Typography>
          {email && (
            <Typography variant="body2" sx={{ color: appColors.text.body }} noWrap>
              {email}
            </Typography>
          )}
          {roleSummary && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: appColors.text.caption,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {roleSummary}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={handleSignOut} aria-label="Sign out of EEAS">
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorMessage(null)} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppHeader;
